import Loader from "components/Loader";
import TextAreaFormField from "components/form/TextAreaFormField";
import { TFunction } from "i18next";
import { FeedbackFormTypeEnum, QAFormType } from "pages/project/common/taskBase.typing";
import { COMMENT_MAX_CHAR_LIMIT, DeliverFormModel } from "pages/project/task/components/modals/useDeliverTaskForm";
import QAForm from "pages/project/task/components/qaForm/qaForm";
import { UseFormRegister, UseFormSetValue, UseFormStateReturn } from "react-hook-form";
import { Trans } from "react-i18next";

const ICRTaskType = "QC 8-VALID";

interface FeedbackFormSectionProps {
  qaForm?: QAFormType;
  isQAFormLoading: boolean;
  qaFormRatings: Array<{
    title: string;
    titleId: number;
    details: string;
    ratings: Array<{ key: number; value: boolean }>;
  }>;
  setValue: UseFormSetValue<DeliverFormModel>;
  feedbackFormType: FeedbackFormTypeEnum;
  shouldShowQAForm: boolean;
  shouldShowMTForm: boolean;
  isStarRating: boolean;
  register: UseFormRegister<DeliverFormModel>;
  formState: UseFormStateReturn<DeliverFormModel>;
  rated3StarsOrLess: boolean;
  taskType?: string;
  isUserSupplier?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  tForm?: (key: string, options?: Record<string, unknown>) => string;
}

export const FeedbackFormSection = ({
  qaForm,
  isQAFormLoading,
  qaFormRatings,
  setValue,
  feedbackFormType,
  shouldShowQAForm,
  shouldShowMTForm,
  isStarRating,
  register,
  formState,
  rated3StarsOrLess,
  taskType,
  isUserSupplier,
  t,
  tForm,
}: FeedbackFormSectionProps) => {
  const translate = tForm || t;
  const isDtp = feedbackFormType === FeedbackFormTypeEnum.DTP;
  const getCommentLabel = () => {
    if (isUserSupplier && taskType === ICRTaskType) {
      return translate("form.comment-icr");
    }
    if (isStarRating) {
      return translate("form.comment-icr");
    }
    if (shouldShowMTForm) {
      return translate("form.mt-feedback-comment");
    }
    return translate("form.qa-feedback-comment");
  };

  const getSectionTitle = () => {
    if (feedbackFormType === FeedbackFormTypeEnum.MT) {
      return translate("form.mt-section-title");
    }
    if (feedbackFormType === FeedbackFormTypeEnum.QA) {
      return translate("form.qa-section-title");
    }
    if (feedbackFormType === FeedbackFormTypeEnum.STAR) {
      return translate("form.star-section-title");
    }
    if (feedbackFormType === FeedbackFormTypeEnum.DTP) {
      return translate("form.dtp-section-title");
    }
    // Handle None case (for DeliverTaskModal)
    if (taskType === ICRTaskType) {
      return translate("form.star-section-title");
    }
    if (shouldShowMTForm) {
      return translate("form.mt-section-title");
    }
    if (taskType && taskType !== ICRTaskType) {
      return translate("form.qa-section-title");
    }
    return "";
  };

  const getSectionDescription = () => {
    if (shouldShowMTForm) {
      return <p className="mt-1 text-sm">{translate("form.mt-section-description")}</p>;
    }
    if (isStarRating) {
      return <p className="mt-1 text-sm">{translate("form.qa-section-qc8-description")}</p>;
    }
    if (isDtp) {
      return <p className="mt-1 text-sm">{translate("form.qa-section-qc6-description")}</p>;
    }
    return (
      <div className="mt-1 text-sm">
        <Trans
          t={translate as TFunction}
          i18nKey="form.qa-section-description"
          components={{
            ul: <ul className="my-2 list-disc space-y-1 pl-5" />,
            li: <li />,
            p: <p className="mb-2" />,
            strong: <strong />,
          }}
        />
      </div>
    );
  };

  return (
    <>
      {/* Form Section (QA/MT/Star/DTP) */}
      <div className="space-y-4">
        <div className="text-left">
          <h4 className="text-sm font-semibold">{getSectionTitle()}</h4>
          {getSectionDescription()}
        </div>

        {shouldShowQAForm || shouldShowMTForm ? (
          <>
            {isQAFormLoading ? (
              <Loader isShown={true} />
            ) : qaForm ? (
              <QAForm
                data={qaForm}
                qaFormRatings={qaFormRatings}
                onqaFormRatingChange={(updatedRatings) => setValue("qaFormRatings", updatedRatings)}
                isStarRating={isStarRating}
              />
            ) : null}
          </>
        ) : null}

        <TextAreaFormField
          id="comment"
          label={getCommentLabel()}
          className="max-h-52"
          autoFocus={true}
          {...register("comment", {
            maxLength: {
              value: COMMENT_MAX_CHAR_LIMIT,
              message: translate("form.commentInvalid", { maxCharLimit: COMMENT_MAX_CHAR_LIMIT }),
            },
            required: {
              value: taskType === ICRTaskType && rated3StarsOrLess,
              message: translate("form.comment-mandatory-required"),
            },
          })}
          error={formState.errors.comment?.message}
          maxCharLimit={COMMENT_MAX_CHAR_LIMIT}
        />
      </div>
    </>
  );
};
